/**
 * Composite item expansion — one composite selection becomes a PARENT line
 * carrying the price plus CHILD lines that are ordinary order items.
 *
 * Sits beside `menuPricingResolver.ts` for the same reason that one does: it is
 * pure, it has a Dart twin in the POS, and both planes must agree exactly. It
 * lives in `models` (below everything) so `core`, `client`, the API and the
 * ordering apps can all reach it — `core` and `client` are siblings and cannot
 * import each other, so a rule that lived in either would be invisible to the
 * other.
 *
 * ─── WHY CHILDREN ARE REAL LINES ───────────────────────────────────────────
 *
 * Every coursing mechanism keys on the ORDER LINE: `Order.courseStates` is
 * keyed by `courseId`, `kitchenTicketEngine` groups printed sections by
 * `courseId`, and `deriveFireJobId` is `(ticketId, printerId, courseId)`. A
 * modifier hangs inside `OrderModifierGroup` on its parent and reaches none of
 * them. Real lines get per-course firing, per-station routing, sales mix and
 * BOM depletion from machinery that already exists.
 *
 * ─── WHAT THIS DELIBERATELY DOES NOT DO ────────────────────────────────────
 *
 * It does NOT resolve course or prep stations. It emits each child's own
 * `menuGroupId`, and the EXISTING resolvers do the rest:
 *   - `courseResolver.resolveCourseId(menuGroupId, item.courseId, groups)`
 *   - `prepStationResolver.resolvePrepStationIds(menuGroupId, item.prepStationIds, groups)`
 * Both already take exactly that shape, both are already server-authoritative
 * on the send path (`buildTicketEngineItems` recomputes stations on every
 * send), and their precedences deliberately DIFFER — course takes the highest
 * branch, stations the nearest ancestor. Re-deriving either here would be a
 * third implementation that silently drifts from both.
 *
 * ─── THE PRICE INVARIANT ───────────────────────────────────────────────────
 *
 * The parent carries the whole price; children are 0 plus their own upcharge.
 * `orderService` sums `item.price` across lines, so the check total is exactly
 * `compositePrice + Σ priceDelta` with no allocation and no rounding — there is
 * no cent to lose. Splitting the price across children would need integer
 * allocation and a remainder rule; that is a later, additive change and no
 * interface here presumes its absence.
 */

import type { IComponentGroup } from './ComponentGroup';
import { isCompositeItem } from './ComponentGroup';
import type { IItem } from './Item';

/** What the guest picked: one component group, and the item ids chosen in it. */
export interface ComponentSelection {
  readonly componentGroupId: number;
  readonly itemIds: readonly number[];
}

/** A line the caller should create. Deliberately transport-agnostic. */
export interface CompositeLineSpec {
  /** The menu item this line rings up. */
  readonly menuItemId: number;
  /** Display name at time of ring-up. */
  readonly name: string;
  /** Unit price in currency units. */
  readonly price: number;
  /**
   * The group this line CLAIMS, for course + prep-station resolution.
   * For a child this is its own home group, never the composite's.
   */
  readonly menuGroupId?: number;
  /** True on the single parent line. */
  readonly isComposite: boolean;
  /**
   * Set on every CHILD, absent on the parent. The caller fills the parent's
   * real `orderItemGuid` in — this module mints no guids, because guid
   * allocation differs per plane (POS local vs server) and a resolver that
   * invented them would be untestable and non-deterministic.
   */
  readonly bundleParentRef?: 'parent';
  /** Which component group this child answered. Absent on the parent. */
  readonly componentGroupId?: number;
}

/** Why an expansion was refused. Rendered by the caller; never thrown. */
export type CompositeRefusal =
  | { readonly kind: 'unknown_group'; readonly componentGroupId: number }
  | {
      readonly kind: 'selection_count';
      readonly componentGroupId: number;
      readonly groupName: string;
      readonly chosen: number;
      readonly minSelection: number;
      readonly maxSelection: number;
    }
  | {
      readonly kind: 'choice_not_offered';
      readonly componentGroupId: number;
      readonly itemId: number;
    }
  | { readonly kind: 'unknown_item'; readonly itemId: number };

export type CompositeExpansion =
  | { readonly ok: true; readonly lines: CompositeLineSpec[] }
  | { readonly ok: false; readonly refusals: CompositeRefusal[] };

/**
 * Expand a composite selection into the lines to create.
 *
 * A NON-composite item returns exactly one line, byte-identical in shape to
 * what an ordinary ring-up produces. That is what makes this safe to put on the
 * unconditional path: nothing about an item with no `componentGroups` changes.
 *
 * @param item          The item being rung up.
 * @param selections    What the guest chose, one entry per answered group.
 * @param itemsById     Every item the choices can point at.
 * @param compositePrice The parent's unit price — resolved by the CALLER through
 *                       `menuPricingResolver`, because group pricing and
 *                       SubgroupReference overrides apply to the composite the
 *                       same as to any item, and duplicating that walk here is
 *                       how two prices for one dish appear.
 */
export function expandComposite(
  item: IItem,
  selections: readonly ComponentSelection[],
  itemsById: ReadonlyMap<number, IItem>,
  compositePrice: number,
): CompositeExpansion {
  if (!isCompositeItem(item)) {
    return {
      ok: true,
      lines: [
        {
          menuItemId: item.itemId ?? 0,
          name: item.name,
          price: compositePrice,
          isComposite: false,
        },
      ],
    };
  }

  const groups: readonly IComponentGroup[] = item.componentGroups ?? [];
  const refusals: CompositeRefusal[] = [];
  const byGroupId = new Map(groups.map((g) => [g.componentGroupId, g]));

  // A selection naming a group this item does not have is a stale client — the
  // operator edited the composite while a terminal held an open picker.
  for (const sel of selections) {
    if (!byGroupId.has(sel.componentGroupId)) {
      refusals.push({ kind: 'unknown_group', componentGroupId: sel.componentGroupId });
    }
  }

  const children: CompositeLineSpec[] = [];

  // Iterate the GROUPS, not the selections: a group the client omitted entirely
  // must still be checked against minSelection, or a required pick silently
  // becomes optional whenever the picker fails to send it.
  for (const group of groups) {
    const chosen = selections.find(
      (s) => s.componentGroupId === group.componentGroupId,
    );
    const chosenIds = chosen?.itemIds ?? [];

    if (
      chosenIds.length < group.minSelection ||
      chosenIds.length > group.maxSelection
    ) {
      refusals.push({
        kind: 'selection_count',
        componentGroupId: group.componentGroupId,
        groupName: group.name,
        chosen: chosenIds.length,
        minSelection: group.minSelection,
        maxSelection: group.maxSelection,
      });
      continue;
    }

    for (const itemId of chosenIds) {
      const choice = group.choices.find((c) => c.itemId === itemId);
      if (!choice) {
        // Refuse rather than ring it up: accepting an item the group does not
        // offer is how a $2 side becomes a $30 steak inside a $14.95 special.
        refusals.push({
          kind: 'choice_not_offered',
          componentGroupId: group.componentGroupId,
          itemId,
        });
        continue;
      }

      const childItem = itemsById.get(itemId);
      if (!childItem) {
        refusals.push({ kind: 'unknown_item', itemId });
        continue;
      }

      children.push({
        menuItemId: itemId,
        name: childItem.name,
        // The child's own upcharge only. The composite's price rides entirely
        // on the parent, so the arithmetic cannot drift by a cent.
        price: choice.priceDelta ?? 0,
        // Its HOME group — never the composite's. See ComponentGroup.ts.
        ...(choice.menuGroupId != null ? { menuGroupId: choice.menuGroupId } : {}),
        isComposite: false,
        bundleParentRef: 'parent',
        componentGroupId: group.componentGroupId,
      });
    }
  }

  if (refusals.length > 0) return { ok: false, refusals };

  return {
    ok: true,
    lines: [
      {
        menuItemId: item.itemId ?? 0,
        name: item.name,
        price: compositePrice,
        // The PARENT deliberately carries NO menuGroupId-driven routing: it is
        // excluded from prep-station resolution entirely (see
        // `isCompositeParentLine`), because an empty prepStationIds is NOT
        // enough — `kitchenTicketEngine` rule 4 sends anything with no stations
        // to the GENERAL catch-all, so the parent would print at the
        // front-of-house printer on every ticket.
        isComposite: true,
      },
      ...children,
    ],
  };
}

/**
 * Does this order line represent a composite PARENT — the priced container that
 * must never reach a prep station or a kitchen ticket?
 *
 * The ONE place the question is answered. `kitchenTicketEngine` has two
 * independent paths that would otherwise print it (rule 1, the expediter
 * summary that takes ALL items regardless of routing; and rule 4, the
 * `GENERAL_STATION_ID` catch-all for items with no stations), so a single
 * predicate both consult is what keeps them from disagreeing.
 */
export function isCompositeParentLine(line: {
  isComposite?: boolean;
}): boolean {
  return line.isComposite === true;
}

/**
 * Does this line belong to a composite, as parent or child? Used by the
 * void / transfer / check-split guards: a bundle moves whole or not at all.
 */
export function isCompositeLine(line: {
  isComposite?: boolean;
  bundleParentGuid?: string;
}): boolean {
  return line.isComposite === true || line.bundleParentGuid != null;
}

/**
 * Every line of one bundle, given any line in it.
 *
 * Returns [] for a line that is not part of a composite, so callers can treat
 * "not a bundle" and "a bundle of one" differently — collapsing them is how a
 * guard starts refusing ordinary voids.
 */
export function bundleSiblings<
  T extends { orderItemGuid: string; isComposite?: boolean; bundleParentGuid?: string },
>(line: T, allLines: readonly T[]): T[] {
  const parentGuid = line.isComposite === true ? line.orderItemGuid : line.bundleParentGuid;
  if (parentGuid == null) return [];
  return allLines.filter(
    (l) => l.orderItemGuid === parentGuid || l.bundleParentGuid === parentGuid,
  );
}
